import * as React from 'react';
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridDataStateChangeEvent,
  GridCustomFilterCellProps,
  GridToolbar,
  isColumnMenuSortActive,
  isColumnMenuFilterActive,
} from '@progress/kendo-react-grid';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { DataResult, process, State } from '@progress/kendo-data-query';
import { Dispatch } from 'redux';
import { useEffect } from 'react';
import {
  LocalizationProvider,
  loadMessages,
  load,
  IntlProvider,
} from '@progress/kendo-react-intl';
import { locales, LocaleInterface } from '../AlertsGrid/GridLocale';
import { getLocalDateTime, getDaysDiff, dateFormat } from '../../utils/utils';
import { LoadingPanel } from '../LoadingPanel';
import { FetchError } from '../../Interfaces/FetchError';
import {
  Checkbox,
  createStyles,
  FormControlLabel,
  makeStyles,
  Theme,
} from '@material-ui/core';
import { Button } from '@progress/kendo-react-buttons';
import { initialVersionsGridDataState } from '../../store/UserSettings/UserSettingsState';
import { Fade } from '@progress/kendo-react-animation';
import {
  Notification,
  NotificationGroup,
} from '@progress/kendo-react-notification';
import { NodeVersionInfo } from '../../Interfaces/Versions/NodeVersionInfo';
import {
  fetchNodeVersionInfo,
  resetNodeVersionInfoFetchError,
} from '../../store/Versions/VersionsActions';
import {
  setExpandedState,
  setGroupIds,
} from '@progress/kendo-react-data-tools';
import { saveVersionsGridState } from '../../store/UserSettings/UserSettingsActions';
import StringResourceManager, { GetString } from '../StringResourceManager';
import esMessages from '../../utils/gridMessageKey.json';
loadMessages(esMessages, 'fr-CA');
import weekData from 'cldr-core/supplemental/weekData.json';
import currencyData from 'cldr-core/supplemental/currencyData.json';
import likelySubtags from 'cldr-core/supplemental/likelySubtags.json';
import caGregorian from 'cldr-dates-full/main/fr/ca-gregorian.json';
import numbers from 'cldr-numbers-full/main/fr/numbers.json';
import dateFields from 'cldr-dates-full/main/fr/dateFields.json';
import timeZones from 'cldr-dates-full/main/fr/timeZoneNames.json';
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
} from '@progress/kendo-data-query';
import {
  ACTIVE_THRESHOLD_DATA,
  MAX_DAYS_FOR_ONLINE_STATUS,
} from '../../utils/constants';
import HealthFilter from './HealthFilter';
import { Label } from '@progress/kendo-react-labels';
import { CSVLink } from 'react-csv';
import { NodeVersionInfoCSV } from '../../Interfaces/Versions/NodeVersionInfoCSV';
import { CustomDateFilterCell } from '../CustomDateFilterCell';
import { UpperCaseStringCell } from '../UpperCaseStringCell';
import {
  ColumnMenu,
  CustomCell,
  CustomColumnMenu,
  HeaderCustomCell,
} from '../ColumnMenus/ColumnMenu';
import { RegistrationStatusCell } from '../Assets/RegistrationStatusCell';
import NodeActionButton from '../NodeActionButton';
import { ApiEnums } from '../../Interfaces/ApiEnums';
import {
  reactivateNode,
  unregisterNode,
} from '../../store/Assets/AssetsActions';
import DynamicDropDownList from '../DynamicDropDownList';
load(
  likelySubtags,
  currencyData,
  weekData,
  caGregorian,
  numbers,
  dateFields,
  timeZones
);

// Styles
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    bottomRight: {
      right: 0,
      bottom: 0,
      alignItems: 'flex-start',
      flexWrap: 'wrap-reverse',
    },
    error: {
      fontSize: '1.5rem',
    },
    toolbar: {
      height: 65,
      alignItems: 'flex-end',
      padding: theme.spacing(1),
      gap: 10,
    },
    toolbarButtonContainer: {
      display: 'grid',
    },
    toolbarButtonGrid: {
      display: 'flex',
    },
    toolbarButton: {
      height: 40,
      marginRight: 9,
    },
    reportingThresholdDropdownContainer: {
      display: 'inline',
      flexDirection: 'row',
      height: 40,
      marginLeft: 5,
      marginTop: 15,
    },
    reportingThresholdDropdownLabel: {
      marginRight: 8,
    },
  })
);

const processWithGroups = (data: any[], dataState: State): DataResult => {
  if (dataState === undefined) {
    dataState = initialVersionsGridDataState;
  }

  let filter: (FilterDescriptor | CompositeFilterDescriptor)[] = [];

  let subFilter: FilterDescriptor[] = [];

  const filters: (CompositeFilterDescriptor | FilterDescriptor)[] | undefined =
    dataState.filter?.filters;

  filters?.map((x: any) => {
    const props = new Map<string, FilterDescriptor[]>(Object.entries(x));
    const newFilters: FilterDescriptor[] = props.get('filters') || [];
    const nodeTypeFilterDescriptor: FilterDescriptor[] = [];
    const nodeTypeFilter: CompositeFilterDescriptor = {
      logic: 'or',
      filters: nodeTypeFilterDescriptor,
    };

    newFilters.map(a => {
      if (Array.isArray(a.value)) {
        for (let i = 0; i < a.value.length; i++) {
          const newValue: FilterDescriptor = {
            field: a?.field,
            operator: a?.operator,
            value: a?.value[i],
          };
          nodeTypeFilterDescriptor.push(newValue);
        }
      }
    });

    if (x.field === 'LastHeartbeat') {
      filter.push(x);
    } else {
      newFilters.map(a => {
        if (Array.isArray(a.value)) {
          filter.push(nodeTypeFilter);
        } else filter.push(x);
      });
    }
  });

  const newFilter: CompositeFilterDescriptor = {
    logic: 'and',
    filters: filter,
  };

  if (subFilter.length > 0)
    newFilter.filters.push({
      logic: 'or',
      filters: subFilter,
    });

  const newDataStateValue: State = {
    ...dataState,
    filter: newFilter,
  };

  const newDataState = process(data, newDataStateValue);

  setGroupIds({ data: newDataState.data, group: newDataStateValue.group });
  return newDataState;
};

interface Props {
  setLastRefreshQuery: (lastRefreshDate: Date) => void;
}

const HealthGrid: React.FC<Props> = ({
  setLastRefreshQuery,
}): React.ReactElement => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const [includeDecommissioned, setIncludeDecommissioned] =
    React.useState(false);

  const [uniqueKeyOnRefresh, setUniqueKeyOnRefresh] = React.useState<Date>(
    new Date()
  );

  const status = GetString('STATUS');

  const csvLinkRef = React.useRef<
    CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
  >(null);

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0]
  );

  const loading: boolean = useSelector(
    (state: ApplicationState) => state.versionsReducer.isFetchingVersionInfo
  );

  const nodeVersionInfo: NodeVersionInfo[] = useSelector(
    (state: ApplicationState) => state.versionsReducer.nodeVersionInfo
  );

  useEffect(() => {
    setResultState(processWithGroups(nodeVersionInfo, dataState));
  }, [nodeVersionInfo]);

  const dataState: State = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.versionsGridDataState
  );

  const fetchError: FetchError = useSelector(
    (state: ApplicationState) => state.versionsReducer.fetchError
  );

  const [collapsedState, setCollapsedState] = React.useState<string[]>([]);

  const [resultState, setResultState] = React.useState<DataResult>(
    processWithGroups(nodeVersionInfo, dataState)
  );

  const [csvData, setCSVData] = React.useState<NodeVersionInfoCSV[]>([]);

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    const result = processWithGroups(nodeVersionInfo, event.dataState);

    saveVersionsGridState(dispatch, event.dataState);
    setResultState(result);
  };

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage
  );

  const [activeThreshold, setActiveThreshold] = React.useState<number>(
    MAX_DAYS_FOR_ONLINE_STATUS
  );

  useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  useEffect(() => {
    let timeoutId: any;
    if (fetchError.StatusCode > 0 && fetchError.Message != '') {
      displayError(true);
      timeoutId = setTimeout(() => {
        clearFetchError();
      }, 3000);
    }

    return (): void => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [fetchError]);

  const [error, displayError] = React.useState(false);

  const clearFetchError = () => {
    displayError(false);
    resetNodeVersionInfoFetchError(dispatch);
  };

  const onFetchNodeVersionInfo = () => {
    let newDataState: State = {
      skip: 0,
      take: dataState.take,
      sort: dataState.sort,
      group: dataState.group,
    };
    setUniqueKeyOnRefresh(new Date());
    saveVersionsGridState(dispatch, newDataState);
    processFetchNodeVersionInfo();
  };

  const [verticalPanes, setVerticalPanes] = React.useState<Array<any>>([
    {},
    {},
  ]);

  const newData = setExpandedState({
    data: resultState.data,
    collapsedIds: collapsedState,
  });

  useEffect(() => {
    processFetchNodeVersionInfo();
  }, []);

  const processFetchNodeVersionInfo = () => {
    fetchNodeVersionInfo(dispatch, includeDecommissioned);
    setLastRefreshQuery(new Date());
  };

  const onChangeActiveThreshold = (event: any) => {
    setActiveThreshold(event.target.value);
  };

  const handleIncludeDecommissionedRecords = () => {
    setIncludeDecommissioned(prevState => {
      const newState = !prevState;
      fetchNodeVersionInfo(dispatch, newState);
      return newState;
    });
  };

  const DateTimeCell = (props: GridCellProps) => {
    const dataItem = props.dataItem;
    if (
      dataItem?.LastHeartbeat &&
      dataItem.LastHeartbeat != '0001-01-01T00:00:00'
    ) {
      if (getDaysDiff(dataItem.LastHeartbeat) < activeThreshold) {
        return (
          <td style={{ backgroundColor: 'rgb(55, 180, 0,0.32)' }}>
            {getLocalDateTime(dataItem.LastHeartbeat)}
          </td>
        );
      } else {
        return (
          <td style={{ backgroundColor: 'rgb(243, 23, 0, 0.32)' }}>
            {getLocalDateTime(dataItem.LastHeartbeat)}
          </td>
        );
      }
    }

    return <td></td>;
  };

  const getCSVFileName = () => {
    return `Health_Status_export_${dateFormat(currentLocale.language)}`;
  };

  const processExportCSV = () => {
    let result: NodeVersionInfoCSV[] = [];

    if (Array.isArray(nodeVersionInfo)) {
      nodeVersionInfo.forEach(nodeAsset => {
        let data: NodeVersionInfoCSV = {
          Division: nodeAsset.Division,
          StoreNumber: nodeAsset.SiteNumber,
          Cluster: nodeAsset.NodeGroup,
          LaneNumber: nodeAsset.LaneNumber,
          MachineName: nodeAsset.MachineName.toUpperCase(),
          Type: nodeAsset.NodeType,
          ASMRole: nodeAsset.ASMRole,
          DeliveryCode: nodeAsset.DeliveryCode,
          BuildNumber: nodeAsset.BuildNumber,
          PackageName: nodeAsset.PackageName,
          VerlessPackageName: nodeAsset.VerlessPackageName,
          Date: getLocalDateTime(nodeAsset.LastHeartbeat),
          ImageVersion: nodeAsset.ImageVersion,
          FreeSpace: nodeAsset.FreeSpace,
          FreeSpace2: nodeAsset.FreeSpace2,
          TimeZone: nodeAsset.TimeZone,
        };
        result.push(data);
      });
    }
    setCSVData(result);
  };

  useEffect(() => {
    exportCSV();
  }, [csvData]);

  const exportCSV = () => {
    if (csvData.length > 0 && csvLinkRef?.current) {
      csvLinkRef.current.link.click();
    }
  };

  const dateFilterCell = (props: GridCustomFilterCellProps) => {
    return (
      <CustomDateFilterCell
        gridFilterCellProps={props}
        dataState={dataState}
        fieldName="LastHeartbeat"
      />
    );
  };

  const handleButtonClick = (
    actionType: ApiEnums.ManagementActions,
    uuid: string
  ) => {
    if (actionType === ApiEnums.ManagementActions.Active) {
      unregisterNode(dispatch, uuid).then(() =>
        fetchNodeVersionInfo(dispatch, includeDecommissioned)
      );
    } else if (actionType === ApiEnums.ManagementActions.Decommissioned) {
      reactivateNode(dispatch, uuid).then(() =>
        fetchNodeVersionInfo(dispatch, includeDecommissioned)
      );
    }
  };

  const NodesActionsCellWrapper = (props: GridCellProps) => {
    const dataItem = props.dataItem;
    if (dataItem?.NodeId) {
      return (
        <td>
          <NodeActionButton
            node={dataItem}
            handleActionButtonClick={handleButtonClick}
          />
        </td>
      );
    }
    return null;
  };

  const columnProps = (field: string) => {
    return {
      field: field,
      columnMenu: ColumnMenu,
      headerClassName: isColumnActive(field, dataState) ? 'active' : '',
    };
  };

  const isColumnActive = (field: string, dataState: State) => {
    return (
      isColumnMenuFilterActive(field, dataState.filter) ||
      isColumnMenuSortActive(field, dataState.sort)
    );
  };

  return (
    <React.Fragment>
      <LocalizationProvider language={currentLocale.language}>
        <IntlProvider locale={currentLocale.locale}>
          <CSVLink
            data={csvData}
            ref={csvLinkRef}
            filename={getCSVFileName() + '.csv'}
          />
          {loading && LoadingPanel}
          <HealthFilter activeThreshold={activeThreshold} />
          <Grid
            key={`${uniqueKeyOnRefresh}`}
            style={{ height: '73vh' }}
            data={resultState}
            {...dataState}
            onDataStateChange={dataStateChange}
            resizable={true}
            sortable={true}
            filterable={true}
            fixedScroll={false}
            pageable={{
              buttonCount: 4,
              pageSizes: [100, 500, 1000, 5000],
            }}
            groupable={true}
            reorderable={true}
            cells={{
              headerCell: HeaderCustomCell,
              data: CustomCell,
            }}
          >
            <GridToolbar>
              <div className={classes.toolbarButtonContainer}>
                <div className={classes.toolbarButtonGrid}>
                  <Button
                    onClick={onFetchNodeVersionInfo}
                    type="submit"
                    className={classes.toolbarButton}
                  >
                    {GetString('RUN_QUERY')}
                  </Button>
                  <Button
                    onClick={processExportCSV}
                    type="submit"
                    className={classes.toolbarButton}
                  >
                    {GetString('EXPORT_TO_CSV')}
                  </Button>
                  <FormControlLabel
                    control={
                      <Checkbox
                        onClick={handleIncludeDecommissionedRecords}
                        name="includeDecommissioned"
                        color="primary"
                        checked={includeDecommissioned}
                      />
                    }
                    label={GetString('INCLUDE_DECOMMISSIONED_RECORDS')}
                  />
                </div>
                <div className={classes.reportingThresholdDropdownContainer}>
                  <Label className={classes.reportingThresholdDropdownLabel}>
                    {GetString('REPORTING_THRESHOLD_DAYS')}:
                  </Label>
                  <DynamicDropDownList
                    onChange={onChangeActiveThreshold}
                    data={ACTIVE_THRESHOLD_DATA}
                    value={activeThreshold}
                    width={150}
                  />
                </div>
              </div>
            </GridToolbar>
            <GridColumn
              {...columnProps('Division')}
              title={GetString('COLUMN_TITLE_DIVISION')}
              width="110px"
              columnMenu={ColumnMenu}
              filterable={false}
            />
            <GridColumn
              {...columnProps('SiteNumber')}
              title={GetString('COLUMN_TITLE_STORE')}
              width="110px"
              columnMenu={ColumnMenu}
              filterable={false}
            />
            <GridColumn
              {...columnProps('NodeGroup')}
              title={GetString('COLUMN_TITLE_CLUSTER')}
              width="110px"
              columnMenu={ColumnMenu}
              filterable={false}
            />
            <GridColumn
              {...columnProps('LaneNumber')}
              title={GetString('COLUMN_TITLE_LANE')}
              width="110px"
              columnMenu={ColumnMenu}
              filterable={false}
            />
            <GridColumn
              {...columnProps('MachineName')}
              title={GetString('COLUMN_TITLE_MACHINE_NAME')}
              width="155px"
              cells={{ data: UpperCaseStringCell }}
              columnMenu={ColumnMenu}
              filterable={false}
            />
            <GridColumn
              {...columnProps('NodeType')}
              title={GetString('COLUMN_TITLE_LANE_TYPE')}
              width="110px"
              columnMenu={props => (
                <CustomColumnMenu {...props} dataState={dataState} />
              )}
              filterable={false}
            />
            <GridColumn
              {...columnProps('ASMRole')}
              title={GetString('ASM_ROLE')}
              width="160px"
              columnMenu={ColumnMenu}
              filterable={false}
            />
            <GridColumn
              {...columnProps('DeliveryCode')}
              title={GetString('DELIVERY_CODE')}
              width="160px"
              columnMenu={ColumnMenu}
              filterable={false}
            />
            <GridColumn
              {...columnProps('BuildNumber')}
              title={GetString('BUILD_NUMBER')}
              width="150px"
              columnMenu={ColumnMenu}
              filterable={false}
            />
            <GridColumn
              {...columnProps('PackageName')}
              title={GetString('PACKAGE_NAME')}
              width="160px"
              columnMenu={ColumnMenu}
              filterable={false}
            />
            <GridColumn
              {...columnProps('VerlessPackageName')}
              title={GetString('VERLESS_PACKAGE_NAME')}
              width="160px"
              columnMenu={ColumnMenu}
              filterable={false}
            />
            <GridColumn
              {...columnProps('LastHeartbeat')}
              title={GetString('COLUMN_TITLE_LANE_UPDATED')}
              width="230px"
              cells={{ data: DateTimeCell, filterCell: dateFilterCell }}
            />
            <GridColumn
              {...columnProps('ImageVersion')}
              title={GetString('IMAGE_VERSION')}
              width="190px"
              columnMenu={ColumnMenu}
              filterable={false}
            />
            <GridColumn
              {...columnProps('FreeSpace')}
              title={GetString('COLUMN_TITLE_FREESPACE')}
              width="180px"
              filter="numeric"
              columnMenu={ColumnMenu}
              filterable={false}
            />
            <GridColumn
              {...columnProps('FreeSpace2')}
              title={GetString('COLUMN_TITLE_FREESPACE_2')}
              width="180px"
              filter="numeric"
              columnMenu={ColumnMenu}
              filterable={false}
            />
            <GridColumn
              {...columnProps('TimeZone')}
              title={GetString('COLUMN_TITLE_TIMEZONE')}
              width="165px"
              columnMenu={ColumnMenu}
              filterable={false}
            />
            <GridColumn
              {...columnProps('ManagementAction')}
              title={GetString('COLUMN_TITLE_MANAGEMENT_ACTION')}
              width="200px"
              cells={{ data: RegistrationStatusCell }}
              filterable={false}
            />
            <GridColumn
              {...columnProps('Actions')}
              title={GetString('COLUMN_TITLE_ACTIONS')}
              width="180px"
              filterable={false}
              cells={{ data: NodesActionsCellWrapper }}
            />
          </Grid>
          <Fade>
            <NotificationGroup className={classes.bottomRight}>
              {error && (
                <Notification
                  className={classes.error}
                  type={{
                    style: 'error',
                    icon: true,
                  }}
                  closable={true}
                  onClose={() => clearFetchError()}
                >
                  <StringResourceManager stringKey={fetchError.Message} />
                  {`(${status}: ${fetchError.StatusCode})`}
                </Notification>
              )}
            </NotificationGroup>
          </Fade>
        </IntlProvider>
      </LocalizationProvider>
    </React.Fragment>
  );
};

export default HealthGrid;
